import * as React from 'react';
import classNames from 'clsx';
import type { IFilesPopperContentProps } from '../../FileUploader.types';
import { testIds } from '../../constants';
import { FileName } from '../FileName/FileName';
import { getFileNameId, getNumberOfFilesLinkId } from '../utils';
import style from './style/FilesPopperContent.scss';

export const FilesPopperContent: React.FunctionComponent<
  IFilesPopperContentProps
> = ({
  files,
  disabled,
  shouldShowValidityIndication,
  onFileRemoval,
  parentCompId,
  popperStyles,
  popperAttributes,
  popperRef,
  isFixedPositionPopper,
  filesFont,
  fileType,
  closePopper,
  translations,
  currentVirtualFocusedItem,
  updateLiveRegion,
  onXIconKeyDown,
}) => {
  const fontOverride = filesFont
    ? ({ '--close_button_fnt': filesFont } as React.CSSProperties)
    : {};

  const handleFileRemoval = (index: number) => {
    onFileRemoval(index);
  };

  const getPopperStyleOverrides = () => {
    let styles = {};

    if (isFixedPositionPopper) {
      styles = {
        inset: 'auto auto 0',
        bottom: 0,
        top: 'auto',
        transform: 'unset',
        position: 'fixed',
      };
    }

    return styles;
  };

  const getAriaActiveDescendant = (index: number) =>
    getFileNameId({ parentCompId, fileIndex: index });

  return (
    // eslint-disable-next-line jsx-a11y/aria-activedescendant-has-tabindex
    <div
      id="popper-content"
      ref={popperRef}
      data-testid={testIds.filesPopper}
      {...popperAttributes}
      className={classNames(style.popper, {
        [style.fixedPositionPopper]: isFixedPositionPopper,
      })}
      style={{
        ...popperStyles,
        ...getPopperStyleOverrides(),
      }}
      role="menu"
      aria-labelledby={getNumberOfFilesLinkId(parentCompId)}
      aria-activedescendant={
        currentVirtualFocusedItem !== undefined
          ? getAriaActiveDescendant(currentVirtualFocusedItem)
          : undefined
      }
    >
      <div
        className={classNames(style.files, {
          [style.fixedPositionPopper]: isFixedPositionPopper,
        })}
        data-testid={testIds.popperFilesWrapper}
      >
        {files.map((file, index) => (
          <FileName
            fileNameId={getAriaActiveDescendant(index)}
            key={`${index}-${file.name}`}
            file={file}
            disabled={disabled}
            shouldShowValidityIndication={shouldShowValidityIndication}
            onFileRemoval={() => handleFileRemoval(index)}
            withCenterEllipsis
            uploadStatus={file.uploadStatus}
            withPopperBehaviour
            isFixedPositionPopper={isFixedPositionPopper}
            filesFont={filesFont}
            translations={translations}
            fileType={fileType}
            virtualFocused={currentVirtualFocusedItem === index}
            updateLiveRegion={updateLiveRegion}
            onXIconKeyDown={e => onXIconKeyDown(index, e)}
          />
        ))}
      </div>
      {isFixedPositionPopper && (
        <>
          <div
            data-testid={testIds.mobileSeperator}
            className={style.seperator}
          />
          <div
            data-testid={testIds.mobileCloseButton}
            style={fontOverride}
            className={style.closeButton}
            onClick={e => closePopper(e)}
          >
            {translations.mobileClosePopper}
          </div>
        </>
      )}
    </div>
  );
};
